<template>
   <form @submit.prevent class="patients-with-operations">
    <rp-select v-if="surgeons.length > 1"
      v-model="surgeonId"
      placeholder="Please select the surgeon who performed the operations">
      <option v-for="surgeon in surgeons" :key="surgeon.id" :value="surgeon.id">
        {{surgeon.profile.first_name + " " + surgeon.profile.last_name}}
      </option>
    </rp-select>
    <rp-patient-operation v-for="entry in resource.operations"
      :key="entry.id"
      :value="entry"
      :iols="iols"
      :trials="trials"
      :locales="locales"
      :hospitals="hospitals"
      :questionnaires="questionnaires"
      :operationTypes="operationTypes"
      :defaultQuestionnaires="defaultQuestionnaires" />
    <rp-button class="add-button" type="secondary" @click="addPatient">
      <rp-sprite class="add-icon" name="control_point" />
      Add another patient
    </rp-button>
    <rp-checkbox
      v-model="resource.accept_terms"
      label="I confirm I have read the consent statement to the patient(s) and received their consent"
      :error="accept_termsError"
      @input="touch('accept_terms')"
      isRequired />
    <rp-button :disabled="isAddPatientInvalid" type="primary" @click="submitOperations">Submit</rp-button>
    <br /><hr /><br />
    <rp-patient-upload
      v-model="resource.csv"
      :locales="locales"
      :hospitals="hospitals"
      :showHospital="showCsvHospital"
      :showLocale="showCsvLocale"
      :operationTypes="operationTypes"
      :templateUrl="templateUrl"
      :advancedTemplateUrl="advancedTemplateUrl"
      :learnUrl="learnUrl"
      :advancedLearnUrl="advancedLearnUrl" />
    <rp-checkbox
      v-model="resource.accept_terms_csv"
      label="I confirm I have read the consent statement to the patient(s) and received their consent"
      :error="accept_terms_csvError"
      @input="touch('accept_terms_csv')"
      isRequired />
    <rp-button :disabled="isUploadCsvInvalid" type="primary" @click="submitCsv">Submit</rp-button>
  </form>
</template>

<script>
  import form from '../../../mixins/form'
  import PatientWithOperation from './PatientWithOperation'
  import PatientWithOperationUpload from './PatientWithOperationUpload'
  import Button from '../../../components/ui/inputs/Button'
  import Checkbox from '../../../components/ui/inputs/Checkbox'
  import Select from '../../../components/ui/inputs/StyledSelect'
  import Sprite from '../../../components/ui/Sprite'
  import {errorMessages} from '../../../api/error_messages'
  import {validationMixin} from 'vuelidate'
  import {date} from '../../../api/validators'
  import {email, required, requiredIf, sameAs} from 'vuelidate/lib/validators'
  import {mapGetters} from 'vuex'

  const validIds = (value) => {
    if (Array.isArray(value)) {
      return (value.includes('welcome') && value.length > 1) ||
        (!value.includes('welcome') && value.length > 0)
    }
    return false
  }

  export default {
    name: 'PatientsWithOperations',
    mixins: [validationMixin, form],

    components: {
      'rp-patient-operation': PatientWithOperation,
      'rp-patient-upload': PatientWithOperationUpload,
      'rp-button': Button,
      'rp-sprite': Sprite,
      'rp-checkbox': Checkbox,
      'rp-select': Select,
    },

    props: {
      iols: Array,
      trials: Array,
      locales: Array,
      hospitals: Array,
      defaultHospitalId: String,
      questionnaires: Array,
      operationTypes: Array,
      defaultQuestionnaires: Array,
      surgeons: Array,
      templateUrl: String,
      advancedTemplateUrl: String,
      learnUrl: String,
      advancedLearnUrl: String,
    },

    data() {
      return {
        surgeonId: '',
        nurseId: '',
      }
    },

    computed: {
      ...mapGetters(['authUser', 'isSurgeon']),
      ...errorMessages(['accept_terms', 'accept_terms_csv']),

      isAddPatientInvalid() {
        return this.$v.resource.operations.$invalid || this.resource.accept_terms === false
      },

      isUploadCsvInvalid() {
        return this.$v.resource.csv.$invalid || this.resource.accept_terms_csv === false
      },

      showCsvHospital(){
        return this.isSurgeon
      },

      showCsvLocale(){
        return true
      },
    },

    mounted() {
      this.nurseId = this.authUser.id
    },

    methods: {
      submitOperations() {
        const data = this.resource.operations.map((operation) => {
          let op = JSON.parse(JSON.stringify(operation))
          op.performed_at = operation.performed_at + 'T12:00:00Z'
          return op
        })
        this.$emit('submitOperations', data)
      },

      submitCsv() {
        this.$emit('submitCsv', this.resource.csv)
      },

      addPatient() {
        this.resource.operations.push({
          id: this.resource.operations.length + 1,
          surgeon_id: this.surgeonId,
          nurse_id: this.nurseId,
          hospital_id: this.defaultHospitalId,
          patient_email: '',
          locale_id: '',
          left_iol_id: '',
          right_iol_id: '',
          questionnaire_ids: [],
          trial_id: '',
          subject_id: '',
          do_trial_enrolment: false,
          operation_type_id: (this.operationTypes.length > 0) ? this.operationTypes[0].id : '',
        })
      },
    },

    watch: {
      surgeons(){
        if (this.surgeons.length === 1) {
          this.surgeonId = this.surgeons[0].id
        }
      },

      surgeonId() {
        this.resource.operations.forEach((operation) => {
          operation.surgeon_id = this.surgeonId
          operation.nurse_id = this.nurseId
          operation.do_trial_enrolment = false
        })
        this.$emit('surgeonChanged', this.surgeonId)
      },

      defaultHospitalId() {
        this.resource.operations.forEach((operation) => {
          operation.hospital_id = this.defaultHospitalId
        })
      }
    },

    validations: {
      resource: {
        accept_terms: {
          required,
          terms: sameAs(() => true)
        },
        accept_terms_csv: {
          required,
          terms: sameAs(() => true)
        },
        operations: {
          required,
          $each: {
            patient_email: {required, email},
            surgeon_id: {required},
            nurse_id: {required},
            locale_id: {required},
            hospital_id: {required},
            performed_at: {required, date},
            questionnaire_ids: {required, validIds},
            operation_type_id: {required},
            left_iol_id: {
              required: requiredIf(function({right_iol_id}) {
                return !right_iol_id
              })
            },
            right_iol_id: {
              required: requiredIf(function({left_iol_id}) {
                return !left_iol_id
              })
            },
            subject_id: {
              required: requiredIf(function({do_trial_enrolment}) {
                return do_trial_enrolment
              })
            },
            trial_id: {
              required: requiredIf(function({do_trial_enrolment}) {
                return do_trial_enrolment
              })
            },
          },
        },
        csv: {
          file: {required},
          hospital_id: {required: requiredIf(function(n) {
            return this.showCsvHospital
          })},
          locale_id: {required: requiredIf(function(n) {
            return this.showCsvLocale
          })},
          operation_type_id: {required},
        },
      },
    },
  }
</script>

<style lang="sass">
  @import '../../../styles/base/variables'
  @import '../../../styles/helpers/mixins'

  .patients-with-operations
    background: $color-panel-bg
    border-radius: 20px
    max-width: 1200px
    padding: 15px 30px 15px 30px

    .add-button
      &.btn
        &.is-secondary
          border: none
          padding-left: 0px
          padding-right: 0px
          font-size: larger

      .add-icon
        margin-right: 6px
</style>



// WEBPACK FOOTER //
// ../raypro_ui/src/components/ui/forms/PatientsWithOperations.vue